import type { ReactNode } from "react";
import type { View } from "../types";

type Props = {
  active: View;
  onNav: (view: View) => void;
  pendingCount: number;
  positionsCount: number;
  ptOverdueCount: number;
  invOverdueCount: number;
};

type ItemArgs = {
  id: View;
  label: string;
  icon: ReactNode;
  badge?: ReactNode;
  active: View;
  onNav: (view: View) => void;
};

const Item = ({ id, label, icon, badge, active, onNav }: ItemArgs) => (
  <a
    className={active === id ? "active" : ""}
    onClick={() => onNav(id)}
    role="link"
    tabIndex={0}
  >
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      {icon}
    </svg>
    <span>{label}</span>
    {badge}
  </a>
);

export const Sidebar = ({
  active,
  onNav,
  pendingCount,
  positionsCount,
  ptOverdueCount,
  invOverdueCount,
}: Props) => (
  <nav className="tp-sidebar">
    <div className="group-label">Session</div>
    <Item
      id="dashboard"
      label="Dashboard"
      icon={
        <>
          <rect x="3" y="3" width="7" height="9" />
          <rect x="14" y="3" width="7" height="5" />
          <rect x="14" y="12" width="7" height="9" />
          <rect x="3" y="16" width="7" height="5" />
        </>
      }
      badge={
        pendingCount > 0 ? (
          <span className="badge alert">{pendingCount}</span>
        ) : null
      }
      active={active}
      onNav={onNav}
    />
    <Item
      id="briefing"
      label="Briefing"
      icon={
        <>
          <path d="M9 11l3 3 8-8" />
          <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
        </>
      }
      active={active}
      onNav={onNav}
    />
    <Item
      id="signal-history"
      label="Signals"
      icon={
        <>
          <path d="M4 19V5" />
          <path d="M4 19h16" />
          <path d="M8 15l3-5 3 2 4-7" />
          <circle cx="8" cy="15" r="1" />
          <circle cx="11" cy="10" r="1" />
          <circle cx="14" cy="12" r="1" />
          <circle cx="18" cy="5" r="1" />
        </>
      }
      active={active}
      onNav={onNav}
    />
    <Item
      id="watchlist"
      label="Watchlist"
      icon={
        <>
          <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
          <circle cx="12" cy="12" r="3" />
        </>
      }
      active={active}
      onNav={onNav}
    />
    <Item
      id="moonshots"
      label="Moonshots"
      icon={
        <>
          <path d="M4 19c4-1 8-5 10-9l4-6 2 2-6 4c-4 2-8 6-9 10z" />
          <path d="M13 5l6 6" />
          <path d="M5 15l4 4" />
          <path d="M3 21l3-1" />
        </>
      }
      active={active}
      onNav={onNav}
    />
    <Item
      id="charts"
      label="Charts"
      icon={
        <>
          <path d="M3 3v18h18" />
          <path d="M7 15l3-3 3 2 5-7" />
          <path d="M8 8v8" />
          <path d="M14 6v10" />
        </>
      }
      active={active}
      onNav={onNav}
    />
    <Item
      id="crypto"
      label="Crypto"
      icon={
        <>
          <path d="M12 2v20" />
          <path d="M17 6.5H9a3 3 0 0 0 0 6h6a3 3 0 0 1 0 6H7" />
          <path d="M8 2v20" />
        </>
      }
      active={active}
      onNav={onNav}
    />
    <Item
      id="forex"
      label="Forex"
      icon={
        // Exchange arrows: two opposing currency flows.
        <>
          <path d="M4 7h13" />
          <path d="M14 4l3 3-3 3" />
          <path d="M20 17H7" />
          <path d="M10 14l-3 3 3 3" />
        </>
      }
      active={active}
      onNav={onNav}
    />

    <div className="group-label">Trading</div>
    <Item
      id="trades"
      label="Day trades"
      icon={
        <>
          <path d="M16 3h5v5" />
          <path d="M21 3l-7 7" />
          <path d="M8 21H3v-5" />
          <path d="M3 21l7-7" />
        </>
      }
      badge={<span className="badge">{positionsCount}</span>}
      active={active}
      onNav={onNav}
    />
    <Item
      id="positions"
      label="Trade history"
      icon={
        <>
          <path d="M3 3v18h18" />
          <path d="M7 14l4-4 3 3 5-6" />
        </>
      }
      badge={
        ptOverdueCount > 0 ? (
          <span className="badge alert">{ptOverdueCount}</span>
        ) : null
      }
      active={active}
      onNav={onNav}
    />

    <div className="group-label">Long term</div>
    <Item
      id="investments"
      label="Investments"
      icon={
        <>
          <path d="M12 2v20" />
          <path d="M5 9h14" />
          <path d="M5 15h14" />
        </>
      }
      badge={
        invOverdueCount > 0 ? (
          <span className="badge alert">{invOverdueCount}</span>
        ) : null
      }
      active={active}
      onNav={onNav}
    />
    <Item
      id="research"
      label="Research"
      icon={
        <>
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.3-4.3" />
        </>
      }
      active={active}
      onNav={onNav}
    />
    <Item
      id="journal"
      label="Journal"
      icon={
        <>
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
        </>
      }
      active={active}
      onNav={onNav}
    />

    <div className="group-label">Operations</div>
    <Item
      id="config"
      label="Config"
      icon={
        <>
          <circle cx="12" cy="12" r="3" />
          <path d="M12 1v6m0 10v6M4.2 4.2l4.3 4.3m7 7l4.3 4.3M1 12h6m10 0h6M4.2 19.8l4.3-4.3m7-7l4.3-4.3" />
        </>
      }
      active={active}
      onNav={onNav}
    />
    <Item
      id="system"
      label="System"
      icon={<path d="M22 12h-4l-3 9L9 3l-3 9H2" />}
      active={active}
      onNav={onNav}
    />
  </nav>
);
